import 'cookie-store';

export enum CookieConsentCategory {
    Advertising = '4',
    Functional = '3',
    Required = '1',
    Unknown = 'unknown',
}

export function isConsentCategoryGranted(category: CookieConsentCategory): boolean {
    return window.OnetrustActiveGroups?.split(',').indexOf(category) !== -1;
}

export function getCookie(name: string): string {
    const cookieString = '; ' + document.cookie;
    const parts = cookieString.split('; ' + name + '=');
    if (parts.length === 2) {
        // @ts-expect-error * Automated Type Error Fix
        return parts.pop().split(';').shift();
    }
    // @ts-expect-error * Automated Type Error Fix
    return null;
}

export function getCookieConsented(name: string, category: CookieConsentCategory): string {
    if (isConsentCategoryGranted(category)) {
        return getCookie(name);
    }
    // @ts-expect-error * Automated Type Error Fix
    return null;
}

/**
 * This method only saves cookies if the category is allowed to be saved based on the user's cookie consent settings.
 * @param name Cookie name
 * @param value Cookie value
 * @param category category of this cookie (required, functional, advertising)
 * @param days expiry time (in days) for this cookie
 */
export function createCatCookie(
    name: string,
    value: string,
    category: CookieConsentCategory,
    days?: number,
    expirationTimestamp?: number,
): void {
    if (isConsentCategoryGranted(category)) {
        createCookie(name, value, days, expirationTimestamp);
    }
}

export function createCookie(name: string, value: string, days?: number, expirationTimestamp?: number): void {
    let expires: string;
    const date = new Date();
    if (days) {
        date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
        expires = `; expires=${date.toUTCString()}`;
    } else if (expirationTimestamp) {
        expires = `; expires=${new Date(+expirationTimestamp).toUTCString()}`;
    } else {
        expires = '';
    }
    // Secure is protocol-conditional (not always-on) so cookies still work in local dev, which
    // runs over plain HTTP; browsers silently drop `Secure` cookies set from a non-HTTPS origin.
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    try {
        document.cookie = `${name}=${value}${expires}; path=/; SameSite=Lax${secure}`;
    } catch (error) {
        // Private-mode / CSP / sandboxed-iframe environments can throw on a cookie write. Swallow so
        // a non-persisted preference never breaks the calling flow. Logged at debug — this is an
        // expected environmental constraint, not a service error. Uses console rather than the app
        // logger on purpose: cookies is a leaf utility imported at the bottom of the graph, and
        // pulling logger (→ @oneappexchange/lwc-wired) in here creates a module-init cycle that
        // leaves the LWC engine half-initialized. Sibling leaf utils log the same way (browser.ts,
        // router.ts).
        console.debug(`cookies.createCookie failed for '${name}'`, error);
    }
}

export async function getCookieExpirationTimestamp(name: string) {
    const cookieInfo = await window.cookieStore.get(name);
    return cookieInfo?.expires;
}
